/**
 * WebSocket Service
 *
 * Manages WebSocket connection with automatic reconnection,
 * message handling, and heartbeat.
 */

import { useConnectionStore } from '@/stores/connectionStore';
import { useQuizStore } from '@/stores/quizStore';
import { useSessionStore } from '@/stores/sessionStore';

// Message types
type MessageType =
  | 'session:create'
  | 'session:join'
  | 'settings:sync'
  | 'question:request'
  | 'answer:submit'
  | 'ping'
  | 'session:created'
  | 'session:joined'
  | 'session:state'
  | 'settings:synced'
  | 'question:new'
  | 'answer:result'
  | 'state:update'
  | 'error'
  | 'pong';

interface WebSocketMessage {
  type: MessageType;
  id: string;
  timestamp: number;
  sessionId?: string;
  payload?: unknown;
}

interface WebSocketServiceConfig {
  maxReconnectAttempts?: number;
  reconnectInterval?: number;
  heartbeatInterval?: number;
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private sessionId: string | null = null;
  private messageHandlers: Map<MessageType, ((payload: unknown) => void)[]> = new Map();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private lastPingTime: number = 0;

  private config: Required<WebSocketServiceConfig> = {
    maxReconnectAttempts: 5,
    reconnectInterval: 3000,
    heartbeatInterval: 30000,
  };

  constructor(config?: WebSocketServiceConfig) {
    if (config) {
      this.config = { ...this.config, ...config };
    }
  }

  /**
   * Connect to WebSocket server
   */
  connect(sessionId: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      // Already connected
      if (this.sessionId === sessionId) return;
      // Different session, disconnect first
      this.disconnect();
    }

    this.sessionId = sessionId;
    useConnectionStore.getState().setStatus('connecting');

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws?sessionId=${sessionId}`;

    try {
      this.ws = new WebSocket(wsUrl);
      this.setupEventHandlers();
    } catch (error) {
      console.error('WebSocket connection error:', error);
      useConnectionStore.getState().setError('Failed to connect');
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.clearTimers();
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.sessionId = null;
    useConnectionStore.getState().onDisconnected();
  }

  /**
   * Send a message to the server
   */
  send(type: MessageType, payload?: unknown): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected, message not sent:', type);
      return;
    }

    const message: WebSocketMessage = {
      type,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      sessionId: this.sessionId || undefined,
      payload,
    };

    this.ws.send(JSON.stringify(message));
  }

  /**
   * Register a handler for a message type
   */
  onMessage<T>(type: MessageType, handler: (payload: T) => void): () => void {
    const handlers = this.messageHandlers.get(type) || [];
    handlers.push(handler as (payload: unknown) => void);
    this.messageHandlers.set(type, handlers);

    // Return unsubscribe function
    return () => {
      const current = this.messageHandlers.get(type) || [];
      const index = current.indexOf(handler as (payload: unknown) => void);
      if (index > -1) {
        current.splice(index, 1);
        this.messageHandlers.set(type, current);
      }
    };
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // Private methods

  private setupEventHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      console.log('WebSocket connected');
      useConnectionStore.getState().onConnected();
      this.startHeartbeat();

      // Join session after connection
      if (this.sessionId) {
        this.send('session:join', { sessionId: this.sessionId });
      }
    };

    this.ws.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason);
      this.clearTimers();

      if (event.code !== 1000) {
        // Abnormal close, attempt reconnect
        this.scheduleReconnect();
      } else {
        useConnectionStore.getState().onDisconnected();
      }
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      useConnectionStore.getState().setError('Connection error');
    };

    this.ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };
  }

  private handleMessage(message: WebSocketMessage): void {
    // Handle pong for latency measurement
    if (message.type === 'pong') {
      const payload = message.payload as { clientTime?: number };
      if (payload?.clientTime) {
        const latency = Math.round((Date.now() - payload.clientTime) / 2);
        useConnectionStore.getState().setLatency(latency);
      }
      return;
    }

    // Handle session state
    if (message.type === 'session:state' || message.type === 'session:joined') {
      const payload = message.payload as { stats?: unknown };
      if (payload?.stats) {
        useSessionStore.getState().updateStats(payload.stats as {
          totalQuestions: number;
          correctAnswers: number;
        });
      }
    }

    // Handle new question
    if (message.type === 'question:new') {
      const payload = message.payload as { question?: unknown };
      if (payload?.question) {
        useQuizStore.getState().setCurrentQuestion(payload.question as {
          id: number;
          topic: string;
          type: string;
          questionMarkdown: string;
          answerMarkdown: string;
          metadata: Record<string, unknown>;
        });
      }
    }

    // Handle answer result
    if (message.type === 'answer:result') {
      const payload = message.payload as { stats?: unknown };
      if (payload?.stats) {
        useSessionStore.getState().updateStats(payload.stats as {
          totalQuestions: number;
          correctAnswers: number;
        });
      }
    }

    // Handle error
    if (message.type === 'error') {
      const payload = message.payload as { message?: string };
      console.error('WebSocket error from server:', payload?.message);
    }

    // Call registered handlers
    const handlers = this.messageHandlers.get(message.type);
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(message.payload);
        } catch (error) {
          console.error('Error in message handler:', error);
        }
      }
    }
  }

  private startHeartbeat(): void {
    this.clearHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.lastPingTime = Date.now();
        this.send('ping', { clientTime: this.lastPingTime });
      }
    }, this.config.heartbeatInterval);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    const store = useConnectionStore.getState();
    const attempts = store.reconnectAttempts;

    if (attempts >= this.config.maxReconnectAttempts) {
      console.log('Max reconnect attempts reached');
      store.setError('Connection lost. Please refresh the page.');
      return;
    }

    store.setStatus('reconnecting');
    store.incrementReconnectAttempts();

    // Exponential backoff
    const delay = this.config.reconnectInterval * Math.pow(2, attempts);

    this.reconnectTimer = setTimeout(() => {
      if (this.sessionId) {
        console.log(`Reconnecting (attempt ${attempts + 1})...`);
        this.connect(this.sessionId);
      }
    }, delay);
  }

  private clearTimers(): void {
    this.clearHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

// Export singleton instance
export const wsService = new WebSocketService();

// Export class for testing
export { WebSocketService };
